import { useMemo, useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell,
} from 'recharts';
import { useAuth } from '@/contexts/AuthContext';
import { useApp } from '@/contexts/AppContext';
import { useInventory } from '@/contexts/InventoryContext';
import { getBookings, getPendingBookings } from '@/utils/bookingStore';
import { getRecentLogs } from '@/utils/activityLogStore';
import { getActiveShift } from '@/utils/shiftStore';
import {
  demoRooms,
  demoPayments,
  demoCanteenBills,
  demoRoomBeds,
} from '@/mocks/demoData';

const ROOM_COLORS = {
  available: '#22c55e',
  occupied: '#ef4444',
  cleaning: '#f59e0b',
  maintenance: '#6b7280',
};

const PIE_COLORS = ['#22c55e', '#ef4444', '#f59e0b', '#6b7280'];

export default function Dashboard() {
  const { user } = useAuth();
  const { addToast } = useApp();
  const { lowStockItems, getAllPendingItems } = useInventory();

  const [lastUpdated, setLastUpdated] = useState<Date>(new Date());
  const [refreshKey, setRefreshKey] = useState(0);
  const [isLive, setIsLive] = useState(true);

  const today = new Date('2026-05-15');
  const todayStr = today.toISOString().slice(0, 10);

  useEffect(() => {
    if (!isLive) return;
    const interval = setInterval(() => {
      setLastUpdated(new Date());
      setRefreshKey((k) => k + 1);
    }, 30000);
    return () => clearInterval(interval);
  }, [isLive]);

  const handleManualRefresh = useCallback(() => {
    setLastUpdated(new Date());
    setRefreshKey((k) => k + 1);
    addToast('info', 'Dashboard refreshed');
  }, [addToast]);

  const getLastUpdatedText = () => {
    const seconds = Math.floor((new Date().getTime() - lastUpdated.getTime()) / 1000);
    if (seconds < 5) return 'Just now';
    if (seconds < 60) return `${seconds}s ago`;
    const minutes = Math.floor(seconds / 60);
    return `${minutes}m ago`;
  };

  const allBookings = getBookings();

  const stats = useMemo(() => {
    const bookings = allBookings;
    const roomStatus = {
      total: demoRooms.length,
      available: demoRooms.filter((r) => r.status === 'available').length,
      occupied: demoRooms.filter((r) => r.status === 'occupied').length,
      cleaning: demoRooms.filter((r) => r.status === 'cleaning').length,
      maintenance: demoRooms.filter((r) => r.status === 'maintenance').length,
    };

    const todayCheckins = bookings.filter((b) => {
      const d = new Date(b.check_in).toISOString().slice(0, 10);
      return d === todayStr && (b.status === 'checked_in' || b.status === 'confirmed');
    }).length;

    const todayCheckouts = bookings.filter((b) => {
      const d = new Date(b.check_out).toISOString().slice(0, 10);
      return d === todayStr && b.status === 'checked_out';
    }).length;

    const todayRevenue = demoPayments
      .filter((p) => new Date(p.created_at).toISOString().slice(0, 10) === todayStr)
      .reduce((sum, p) => sum + p.amount, 0);

    const canteenToday = demoCanteenBills
      .filter((b) => new Date(b.created_at).toISOString().slice(0, 10) === todayStr && b.status === 'paid')
      .reduce((sum, b) => sum + b.total, 0);

    const pendingPayments = bookings
      .filter((b) => b.pending_amount > 0)
      .reduce((sum, b) => sum + b.pending_amount, 0);

    const lowStock = lowStockItems.length;

    const pendingItems = getAllPendingItems();
    const vendorPendingCount = pendingItems.length;
    const vendorPendingValue = pendingItems.reduce((s, pi) => s + pi.qtyPending * 0, 0);

    const activeShift = getActiveShift();
    const shiftCollections = activeShift ? activeShift.collections : 0;

    const occupancyRate = Math.round((roomStatus.occupied / roomStatus.total) * 100);

    const dormRooms = demoRooms.filter((r) => r.room_type === 'dormitory' || r.room_type === 'shared');
    const totalBeds = demoRoomBeds.length;
    const occupiedBeds = demoRoomBeds.filter((b) => b.status === 'occupied').length;
    const availableBeds = demoRoomBeds.filter((b) => b.status === 'available').length;
    const dormTotalBeds = demoRoomBeds.filter((b) => dormRooms.some((r) => r.id === b.room_id)).length;
    const dormOccupiedBeds = demoRoomBeds.filter((b) => b.status === 'occupied' && dormRooms.some((r) => r.id === b.room_id)).length;
    const dormOccupancy = dormTotalBeds > 0 ? Math.round((dormOccupiedBeds / dormTotalBeds) * 100) : 0;

    const bedRevenue = bookings
      .filter((b) => b.is_bed_booking && (b.status === 'checked_in' || b.status === 'confirmed'))
      .reduce((sum, b) => sum + b.total_amount, 0);

    const bedStatusData = {
      available: demoRoomBeds.filter((b) => b.status === 'available').length,
      occupied: demoRoomBeds.filter((b) => b.status === 'occupied').length,
      reserved: demoRoomBeds.filter((b) => b.status === 'reserved').length,
      cleaning: demoRoomBeds.filter((b) => b.status === 'cleaning').length,
      maintenance: demoRoomBeds.filter((b) => b.status === 'maintenance').length,
    };

    const dormRoomsFull = demoRooms.filter((r) => r.room_type === 'dormitory' || r.room_type === 'shared');
    const cleaningRooms = demoRooms.filter((r) => r.status === 'cleaning');
    const maintenanceRooms = demoRooms.filter((r) => r.status === 'maintenance');
    const dormFullyBooked = dormRoomsFull.filter((r) => {
      const roomBeds = demoRoomBeds.filter((b) => b.room_id === r.id);
      return roomBeds.every((b) => b.status === 'occupied' || b.status === 'reserved');
    }).length;
    const dormLowBeds = dormRoomsFull.filter((r) => {
      const roomBeds = demoRoomBeds.filter((b) => b.room_id === r.id);
      const avail = roomBeds.filter((b) => b.status === 'available').length;
      return avail > 0 && avail <= 2;
    }).length;
    const bedsUnderMaintenance = demoRoomBeds.filter((b) => b.status === 'maintenance').length;

    const recentLogs = getRecentLogs(10);

    return {
      roomStatus,
      todayCheckins,
      todayCheckouts,
      todayRevenue,
      canteenToday,
      pendingPayments,
      lowStock,
      vendorPendingCount,
      vendorPendingValue,
      shiftCollections,
      occupancyRate,
      totalBeds,
      occupiedBeds,
      availableBeds,
      dormOccupancy,
      bedRevenue,
      bedStatusData,
      dormFullyBooked,
      dormLowBeds,
      bedsUnderMaintenance,
      cleaningRooms,
      maintenanceRooms,
      recentLogs,
    };
  }, [todayStr, refreshKey, lowStockItems, getAllPendingItems, allBookings]);

  const roomPieData = [
    { name: 'Available', value: stats.roomStatus.available, color: ROOM_COLORS.available },
    { name: 'Occupied', value: stats.roomStatus.occupied, color: ROOM_COLORS.occupied },
    { name: 'Cleaning', value: stats.roomStatus.cleaning, color: ROOM_COLORS.cleaning },
    { name: 'Maintenance', value: stats.roomStatus.maintenance, color: ROOM_COLORS.maintenance },
  ];

  const revenueData = [
    { day: 'Mon', rooms: 8500, canteen: 1200, beds: 1800 },
    { day: 'Tue', rooms: 10200, canteen: 1800, beds: 2400 },
    { day: 'Wed', rooms: 7800, canteen: 950, beds: 1200 },
    { day: 'Thu', rooms: 12400, canteen: 2100, beds: 3600 },
    { day: 'Fri', rooms: 15600, canteen: 3200, beds: 4200 },
    { day: 'Sat', rooms: 18900, canteen: 4500, beds: 4800 },
    { day: 'Sun', rooms: 21300, canteen: 5100, beds: 5400 },
  ];

  const quickActions = [
    {
      label: 'New Booking',
      path: '/bookings/new',
      icon: 'ri-add-circle-line',
      color: 'bg-navy-700 text-white hover:bg-navy-800',
      iconColor: 'text-white',
    },
    {
      label: 'Check-in',
      path: '/reception/checkin',
      icon: 'ri-login-box-line',
      color: 'bg-green-50 text-green-700 hover:bg-green-100 border border-green-200',
      iconColor: 'text-green-600',
    },
    {
      label: 'Check-out',
      path: '/reception/checkout',
      icon: 'ri-logout-box-line',
      color: 'bg-red-50 text-red-700 hover:bg-red-100 border border-red-200',
      iconColor: 'text-red-600',
    },
    {
      label: 'Canteen Bill',
      path: '/canteen/billing',
      icon: 'ri-restaurant-line',
      color: 'bg-gold-50 text-gold-700 hover:bg-gold-100 border border-gold-200',
      iconColor: 'text-gold-600',
    },
  ];

  const getActionIcon = (action: string) => {
    if (action.includes('Bed Check-in') || action.includes('Dormitory Check-in')) return 'ri-hotel-bed-line text-teal-500';
    if (action.includes('Bed Check-out')) return 'ri-logout-box-line text-teal-600';
    if (action.includes('Bed Booking')) return 'ri-calendar-check-line text-teal-500';
    if (action.includes('Check-in')) return 'ri-login-box-line text-green-500';
    if (action.includes('Check-out')) return 'ri-logout-box-line text-red-500';
    if (action.includes('Payment')) return 'ri-money-rupee-circle-line text-gold-500';
    if (action.includes('Canteen')) return 'ri-restaurant-line text-orange-500';
    if (action.includes('Booking')) return 'ri-calendar-check-line text-navy-500';
    if (action.includes('Shift')) return 'ri-time-line text-navy-400';
    if (action.includes('Stock')) return 'ri-box-3-line text-blue-500';
    return 'ri-file-list-line text-navy-400';
  };

  const getActionBg = (action: string) => {
    if (action.includes('Bed') || action.includes('Dormitory')) return 'bg-teal-50';
    if (action.includes('Check-in')) return 'bg-green-50';
    if (action.includes('Check-out')) return 'bg-red-50';
    if (action.includes('Payment')) return 'bg-gold-50';
    if (action.includes('Canteen')) return 'bg-orange-50';
    if (action.includes('Booking')) return 'bg-navy-50';
    if (action.includes('Shift')) return 'bg-navy-50';
    if (action.includes('Stock')) return 'bg-blue-50';
    return 'bg-navy-50';
  };

  const handleQuickAction = (label: string) => {
    addToast('info', `${label} clicked - navigate to module`);
  };

  return (
    <div>
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-semibold text-navy-800">
              Welcome back, {user?.full_name?.split(' ')[0]}
            </h1>
            <p className="text-navy-500 mt-1">Here is what is happening at your hotel today.</p>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1.5 text-xs text-navy-400">
              {isLive ? (
                <>
                  <span className="relative flex h-2 w-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" />
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500" />
                  </span>
                  <span className="text-green-600 font-medium">Live</span>
                  <span className="text-navy-400 ml-1">· {getLastUpdatedText()}</span>
                </>
              ) : (
                <span className="text-navy-400">Auto-refresh paused</span>
              )}
            </div>
            <button
              onClick={() => setIsLive(!isLive)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium cursor-pointer whitespace-nowrap transition-colors ${isLive ? 'bg-green-50 text-green-700 hover:bg-green-100 border border-green-200' : 'bg-navy-50 text-navy-500 hover:bg-navy-100 border border-navy-200'}`}
            >
              <i className={`${isLive ? 'ri-pause-circle-line' : 'ri-play-circle-line'} mr-1`} />
              {isLive ? 'Pause' : 'Resume'}
            </button>
            <button
              onClick={handleManualRefresh}
              className="px-3 py-1.5 rounded-lg text-xs font-medium bg-navy-50 text-navy-600 hover:bg-navy-100 border border-navy-200 cursor-pointer whitespace-nowrap transition-colors"
            >
              <i className="ri-refresh-line mr-1" />Refresh
            </button>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        {quickActions.map((action) => (
          <Link
            key={action.label}
            to={action.path}
            onClick={() => handleQuickAction(action.label)}
            className={`flex items-center gap-3 px-4 py-3.5 rounded-xl transition-all cursor-pointer ${action.color}`}
          >
            <span className={`w-10 h-10 rounded-lg flex items-center justify-center bg-white/60 ${action.iconColor}`}>
              <i className={`${action.icon} text-lg`} />
            </span>
            <span className="font-semibold text-sm whitespace-nowrap">{action.label}</span>
          </Link>
        ))}
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {/* Rooms */}
        <Link to="/bookings/rooms" className="bg-white rounded-xl border border-navy-100 p-5 hover:border-navy-300 hover:shadow-sm transition-all cursor-pointer block group">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm font-medium text-navy-500">Total Rooms</span>
            <span className="w-9 h-9 rounded-lg bg-navy-50 flex items-center justify-center group-hover:bg-navy-100 transition-colors">
              <i className="ri-hotel-bed-line text-navy-600" />
            </span>
          </div>
          <div className="text-2xl font-bold text-navy-800">{stats.roomStatus.total}</div>
          <div className="flex items-center gap-3 mt-2 text-xs">
            <span className="flex items-center gap-1 text-green-600">
              <span className="w-2 h-2 rounded-full bg-green-500" />
              {stats.roomStatus.available} Avail
            </span>
            <span className="flex items-center gap-1 text-red-500">
              <span className="w-2 h-2 rounded-full bg-red-500" />
              {stats.roomStatus.occupied} Occ
            </span>
          </div>
        </Link>

        {/* Today Check-ins */}
        <Link to="/reception/checkin" className="bg-white rounded-xl border border-navy-100 p-5 hover:border-navy-300 hover:shadow-sm transition-all cursor-pointer block group">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm font-medium text-navy-500">Today Check-ins</span>
            <span className="w-9 h-9 rounded-lg bg-green-50 flex items-center justify-center group-hover:bg-green-100 transition-colors">
              <i className="ri-login-box-line text-green-600" />
            </span>
          </div>
          <div className="text-2xl font-bold text-navy-800">{stats.todayCheckins}</div>
          <div className="text-xs text-green-600 mt-2">
            {stats.todayCheckouts} check-outs today
          </div>
        </Link>

        {/* Revenue */}
        <Link to="/reception/reports" className="bg-white rounded-xl border border-navy-100 p-5 hover:border-navy-300 hover:shadow-sm transition-all cursor-pointer block group">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm font-medium text-navy-500">Today Revenue</span>
            <span className="w-9 h-9 rounded-lg bg-gold-50 flex items-center justify-center group-hover:bg-gold-100 transition-colors">
              <i className="ri-money-rupee-circle-line text-gold-600" />
            </span>
          </div>
          <div className="text-2xl font-bold text-navy-800">
            Rs. {(stats.todayRevenue + stats.canteenToday).toLocaleString('en-IN')}
          </div>
          <div className="text-xs text-navy-400 mt-2">
            Rooms: Rs. {stats.todayRevenue.toLocaleString('en-IN')} + Canteen: Rs. {stats.canteenToday.toLocaleString('en-IN')}
          </div>
        </Link>

        {/* Pending Payments */}
        <Link to="/reception/billing" className="bg-white rounded-xl border border-navy-100 p-5 hover:border-red-200 hover:shadow-sm transition-all cursor-pointer block group">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm font-medium text-navy-500">Pending Payments</span>
            <span className="w-9 h-9 rounded-lg bg-red-50 flex items-center justify-center group-hover:bg-red-100 transition-colors">
              <i className="ri-error-warning-line text-red-500" />
            </span>
          </div>
          <div className="text-2xl font-bold text-red-600">
            Rs. {stats.pendingPayments.toLocaleString('en-IN')}
          </div>
          <div className="text-xs text-navy-400 mt-2">
            {allBookings.filter((b) => b.pending_amount > 0).length} bookings pending
          </div>
        </Link>

        {/* Occupancy Rate */}
        <Link to="/bookings/rooms" className="bg-white rounded-xl border border-navy-100 p-5 hover:border-navy-300 hover:shadow-sm transition-all cursor-pointer block group">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm font-medium text-navy-500">Occupancy Rate</span>
            <span className="w-9 h-9 rounded-lg bg-blue-50 flex items-center justify-center group-hover:bg-blue-100 transition-colors">
              <i className="ri-percent-line text-blue-600" />
            </span>
          </div>
          <div className="text-2xl font-bold text-navy-800">{stats.occupancyRate}%</div>
          <div className="w-full bg-navy-100 rounded-full h-2 mt-2">
            <div
              className="bg-navy-600 h-2 rounded-full transition-all"
              style={{ width: `${stats.occupancyRate}%` }}
            />
          </div>
        </Link>

        {/* Shift Collection */}
        <Link to="/reception/shifts" className="bg-white rounded-xl border border-navy-100 p-5 hover:border-navy-300 hover:shadow-sm transition-all cursor-pointer block group">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm font-medium text-navy-500">Shift Collections</span>
            <span className="w-9 h-9 rounded-lg bg-purple-50 flex items-center justify-center group-hover:bg-purple-100 transition-colors">
              <i className="ri-cash-line text-purple-600" />
            </span>
          </div>
          <div className="text-2xl font-bold text-navy-800">
            Rs. {stats.shiftCollections.toLocaleString('en-IN')}
          </div>
          <div className="text-xs text-navy-400 mt-2">
            Evening Shift (2PM - 10PM)
          </div>
        </Link>

        {/* Low Stock */}
        <Link to="/inventory/stock" className="bg-white rounded-xl border border-navy-100 p-5 hover:border-amber-200 hover:shadow-sm transition-all cursor-pointer block group">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm font-medium text-navy-500">Low Stock Alerts</span>
            <span className="w-9 h-9 rounded-lg bg-amber-50 flex items-center justify-center group-hover:bg-amber-100 transition-colors">
              <i className="ri-box-3-line text-amber-600" />
            </span>
          </div>
          <div className={`text-2xl font-bold ${stats.lowStock > 0 ? 'text-amber-600' : 'text-green-600'}`}>
            {stats.lowStock}
          </div>
          <div className="text-xs text-navy-400 mt-2">
            Items below minimum level
          </div>
        </Link>

        {/* Vendor Pending */}
        <Link to="/inventory/purchases" className="bg-white rounded-xl border border-red-100 p-5 hover:border-red-200 hover:shadow-sm transition-all cursor-pointer block group">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm font-medium text-navy-500">Vendor Pending</span>
            <span className="w-9 h-9 rounded-lg bg-red-50 flex items-center justify-center group-hover:bg-red-100 transition-colors">
              <i className="ri-truck-line text-red-500" />
            </span>
          </div>
          <div className={`text-2xl font-bold ${stats.vendorPendingCount > 0 ? 'text-red-600' : 'text-green-600'}`}>
            {stats.vendorPendingCount}
          </div>
          <div className="text-xs text-navy-400 mt-2">
            Items pending delivery from vendors
          </div>
        </Link>

        {/* Canteen Sales */}
        <Link to="/canteen/billing" className="bg-white rounded-xl border border-navy-100 p-5 hover:border-navy-300 hover:shadow-sm transition-all cursor-pointer block group">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm font-medium text-navy-500">Canteen Sales</span>
            <span className="w-9 h-9 rounded-lg bg-orange-50 flex items-center justify-center group-hover:bg-orange-100 transition-colors">
              <i className="ri-restaurant-line text-orange-600" />
            </span>
          </div>
          <div className="text-2xl font-bold text-navy-800">
            Rs. {stats.canteenToday.toLocaleString('en-IN')}
          </div>
          <div className="text-xs text-navy-400 mt-2">
            {demoCanteenBills.filter((b) => new Date(b.created_at).toISOString().slice(0, 10) === todayStr).length} bills today
          </div>
        </Link>

        {/* Total Beds */}
        <Link to="/bookings/rooms" className="bg-white rounded-xl border border-gold-200 p-5 hover:border-gold-300 hover:shadow-sm transition-all cursor-pointer block group">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm font-medium text-navy-500">Total Beds</span>
            <span className="w-9 h-9 rounded-lg bg-gold-50 flex items-center justify-center group-hover:bg-gold-100 transition-colors">
              <i className="ri-hotel-bed-line text-gold-600" />
            </span>
          </div>
          <div className="text-2xl font-bold text-navy-800">{stats.totalBeds}</div>
          <div className="flex items-center gap-3 mt-2 text-xs">
            <span className="flex items-center gap-1 text-green-600">
              <span className="w-2 h-2 rounded-full bg-green-500" />
              {stats.availableBeds} Avail
            </span>
            <span className="flex items-center gap-1 text-red-500">
              <span className="w-2 h-2 rounded-full bg-red-500" />
              {stats.occupiedBeds} Occ
            </span>
          </div>
        </Link>

        {/* Occupied Beds */}
        <Link to="/bookings/rooms" className="bg-white rounded-xl border border-navy-100 p-5 hover:border-red-200 hover:shadow-sm transition-all cursor-pointer block group">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm font-medium text-navy-500">Occupied Beds</span>
            <span className="w-9 h-9 rounded-lg bg-red-50 flex items-center justify-center group-hover:bg-red-100 transition-colors">
              <i className="ri-user-line text-red-500" />
            </span>
          </div>
          <div className="text-2xl font-bold text-red-600">{stats.occupiedBeds}</div>
          <div className="text-xs text-navy-400 mt-2">
            {stats.totalBeds - stats.occupiedBeds} beds available
          </div>
        </Link>

        {/* Available Beds */}
        <Link to="/bookings/rooms" className="bg-white rounded-xl border border-navy-100 p-5 hover:border-green-200 hover:shadow-sm transition-all cursor-pointer block group">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm font-medium text-navy-500">Available Beds</span>
            <span className="w-9 h-9 rounded-lg bg-green-50 flex items-center justify-center group-hover:bg-green-100 transition-colors">
              <i className="ri-check-line text-green-600" />
            </span>
          </div>
          <div className="text-2xl font-bold text-green-600">{stats.availableBeds}</div>
          <div className="text-xs text-navy-400 mt-2">
            Ready for check-in
          </div>
        </Link>

        {/* Dormitory Occupancy % */}
        <Link to="/bookings/rooms" className="bg-white rounded-xl border border-navy-100 p-5 hover:border-navy-300 hover:shadow-sm transition-all cursor-pointer block group">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm font-medium text-navy-500">Dormitory Occupancy</span>
            <span className="w-9 h-9 rounded-lg bg-purple-50 flex items-center justify-center group-hover:bg-purple-100 transition-colors">
              <i className="ri-building-line text-purple-600" />
            </span>
          </div>
          <div className="text-2xl font-bold text-navy-800">{stats.dormOccupancy}%</div>
          <div className="w-full bg-navy-100 rounded-full h-2 mt-2">
            <div
              className="bg-gold-500 h-2 rounded-full transition-all"
              style={{ width: `${stats.dormOccupancy}%` }}
            />
          </div>
        </Link>

        {/* Bed Revenue */}
        <Link to="/reception/reports" className="bg-white rounded-xl border border-gold-200 p-5 hover:border-gold-300 hover:shadow-sm transition-all cursor-pointer block group">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm font-medium text-navy-500">Bed Revenue</span>
            <span className="w-9 h-9 rounded-lg bg-gold-50 flex items-center justify-center group-hover:bg-gold-100 transition-colors">
              <i className="ri-money-rupee-circle-line text-gold-600" />
            </span>
          </div>
          <div className="text-2xl font-bold text-navy-800">
            Rs. {stats.bedRevenue.toLocaleString('en-IN')}
          </div>
          <div className="text-xs text-navy-400 mt-2">
            {allBookings.filter((b) => b.is_bed_booking && b.status === 'checked_in').length} active bed stays
          </div>
        </Link>

        {/* Rooms to Clean */}
        <Link to="/bookings/rooms" className="bg-white rounded-xl border border-yellow-200 p-5 hover:border-yellow-300 hover:shadow-sm transition-all cursor-pointer block group">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm font-medium text-navy-500">Rooms to Clean</span>
            <span className="w-9 h-9 rounded-lg bg-yellow-50 flex items-center justify-center group-hover:bg-yellow-100 transition-colors">
              <i className="ri-brush-line text-yellow-600" />
            </span>
          </div>
          <div className={`text-2xl font-bold ${stats.cleaningRooms.length > 0 ? 'text-yellow-600' : 'text-green-600'}`}>
            {stats.cleaningRooms.length}
          </div>
          <div className="text-xs text-navy-400 mt-2">
            {stats.cleaningRooms.length > 0
              ? `${stats.cleaningRooms.map((r) => r.room_number).join(', ')}`
              : 'All rooms are ready'}
          </div>
          {stats.cleaningRooms.length > 0 && (
            <div className="flex items-center gap-1 mt-2.5 text-xs text-yellow-600 font-medium">
              <i className="ri-arrow-right-line" /> View & mark ready
            </div>
          )}
        </Link>
      </div>

      {/* Charts + Activity Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        {/* Revenue Chart */}
        <div className="bg-white rounded-xl border border-navy-100 p-5 lg:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-navy-800">Weekly Revenue Trend</h3>
            <div className="flex items-center gap-4 text-xs">
              <span className="flex items-center gap-1">
                <span className="w-3 h-3 rounded-sm bg-navy-600" />
                Rooms
              </span>
              <span className="flex items-center gap-1">
                <span className="w-3 h-3 rounded-sm bg-gold-500" />
                Canteen
              </span>
              <span className="flex items-center gap-1">
                <span className="w-3 h-3 rounded-sm bg-teal-500" />
                Beds
              </span>
            </div>
          </div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={revenueData} barGap={4}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
                <XAxis dataKey="day" axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#6b7280' }} />
                <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#6b7280' }} />
                <Tooltip
                  contentStyle={{ borderRadius: '8px', border: '1px solid #e5e7eb', fontSize: '12px' }}
                  formatter={(value: number) => [`Rs. ${value.toLocaleString('en-IN')}`, '']}
                />
                <Bar dataKey="rooms" fill="#1a2b4a" radius={[4, 4, 0, 0]} maxBarSize={36} />
                <Bar dataKey="canteen" fill="#c9a84c" radius={[4, 4, 0, 0]} maxBarSize={36} />
                <Bar dataKey="beds" fill="#14b8a6" radius={[4, 4, 0, 0]} maxBarSize={36} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Room Status Pie */}
        <div className="bg-white rounded-xl border border-navy-100 p-5">
          <h3 className="font-semibold text-navy-800 mb-4">Room Status</h3>
          <div className="h-48">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={roomPieData}
                  cx="50%"
                  cy="50%"
                  innerRadius={50}
                  outerRadius={80}
                  paddingAngle={3}
                  dataKey="value"
                >
                  {roomPieData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={PIE_COLORS[index]} />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{ borderRadius: '8px', border: '1px solid #e5e7eb', fontSize: '12px' }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="grid grid-cols-2 gap-2 mt-2">
            {roomPieData.map((item) => (
              <div key={item.name} className="flex items-center gap-2 text-xs">
                <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: item.color }} />
                <span className="text-navy-600">{item.name}</span>
                <span className="font-semibold text-navy-800 ml-auto">{item.value}</span>
              </div>
            ))}
          </div>
          <div className="mt-4 pt-3 border-t border-navy-100">
            <div className="text-xs font-semibold text-navy-700 mb-2">Bed Status</div>
            <div className="grid grid-cols-2 gap-1.5">
              <div className="flex items-center gap-2 text-xs">
                <span className="w-2.5 h-2.5 rounded-full flex-shrink-0 bg-green-500" />
                <span className="text-navy-600">Available</span>
                <span className="font-semibold text-green-700 ml-auto">{stats.bedStatusData.available}</span>
              </div>
              <div className="flex items-center gap-2 text-xs">
                <span className="w-2.5 h-2.5 rounded-full flex-shrink-0 bg-red-500" />
                <span className="text-navy-600">Occupied</span>
                <span className="font-semibold text-red-600 ml-auto">{stats.bedStatusData.occupied}</span>
              </div>
              <div className="flex items-center gap-2 text-xs">
                <span className="w-2.5 h-2.5 rounded-full flex-shrink-0 bg-amber-400" />
                <span className="text-navy-600">Reserved</span>
                <span className="font-semibold text-amber-600 ml-auto">{stats.bedStatusData.reserved}</span>
              </div>
              <div className="flex items-center gap-2 text-xs">
                <span className="w-2.5 h-2.5 rounded-full flex-shrink-0 bg-gray-400" />
                <span className="text-navy-600">Cleaning</span>
                <span className="font-semibold text-gray-500 ml-auto">{stats.bedStatusData.cleaning}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Row: Recent Activity + Alerts */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Recent Activity */}
        <div className="bg-white rounded-xl border border-navy-100 p-5 lg:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-navy-800">Recent Activity</h3>
            <Link to="/reception/logs" className="text-sm text-navy-500 hover:text-navy-700">
              View all <i className="ri-arrow-right-line" />
            </Link>
          </div>
          <div className="space-y-3">
            {stats.recentLogs.map((log) => (
              <div key={log.id} className="flex items-start gap-3 p-3 rounded-lg hover:bg-navy-50 transition-colors">
                <div className={`w-9 h-9 rounded-lg ${getActionBg(log.action)} flex items-center justify-center flex-shrink-0`}>
                  <i className={`${getActionIcon(log.action)} text-base`} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium text-sm text-navy-800">{log.action}</span>
                    <span className="text-xs text-navy-400">by {log.user_name}</span>
                  </div>
                  <div className="text-xs text-navy-500 mt-0.5">
                    {log.entity_type === 'bed_booking' && log.details
                      ? `Room: ${(log.details as { room?: string }).room}, ${(log.details as { bed?: string }).bed}`
                      : log.entity_type === 'booking' && log.details && (log.details as { guest?: string; room?: string }).guest
                      ? `Guest: ${(log.details as { guest?: string; room?: string }).guest}, Room: ${(log.details as { guest?: string; room?: string }).room}`
                      : log.entity_type === 'payment' && log.details && (log.details as { amount?: number }).amount
                      ? `Amount: Rs. ${(log.details as { amount?: number }).amount?.toLocaleString('en-IN')}`
                      : log.entity_type === 'canteen_bill' && log.details && (log.details as { room?: string; table?: string; total?: number }).room
                      ? `Room: ${(log.details as { room?: string }).room}, Total: Rs. ${(log.details as { total?: number }).total}`
                      : log.entity_type === 'canteen_bill' && log.details && (log.details as { table?: string }).table
                      ? `Table: ${(log.details as { table?: string }).table}, Total: Rs. ${(log.details as { total?: number }).total}`
                      : log.entity_type === 'shift' && log.details && (log.details as { shift?: string; collections?: number }).shift
                      ? `${(log.details as { shift?: string }).shift} shift, Collections: Rs. ${(log.details as { collections?: number }).collections}`
                      : `${log.entity_type} #${log.entity_id.slice(-3)}`}
                  </div>
                </div>
                <div className="text-xs text-navy-400 whitespace-nowrap flex-shrink-0">
                  {new Date(log.created_at).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Alerts Panel */}
        <div className="bg-white rounded-xl border border-navy-100 p-5">
          <h3 className="font-semibold text-navy-800 mb-4">Alerts & Notifications</h3>
          <div className="space-y-3">
            {stats.lowStock > 0 && (
              <div className="flex items-start gap-3 p-3 rounded-lg bg-amber-50 border border-amber-100">
                <i className="ri-box-3-line text-amber-600 text-lg mt-0.5" />
                <div>
                  <div className="text-sm font-medium text-amber-800">{stats.lowStock} items low on stock</div>
                  <div className="text-xs text-amber-600 mt-0.5">
                    {lowStockItems.slice(0, 3).map((i) => i.name).join(', ')}{stats.lowStock > 3 ? `, and ${stats.lowStock - 3} more` : ''}
                  </div>
                  <Link to="/inventory/stock" className="text-xs text-amber-700 underline mt-1 inline-block">
                    View inventory
                  </Link>
                </div>
              </div>
            )}

            {stats.vendorPendingCount > 0 && (
              <div className="flex items-start gap-3 p-3 rounded-lg bg-red-50 border border-red-100">
                <i className="ri-truck-line text-red-500 text-lg mt-0.5" />
                <div>
                  <div className="text-sm font-medium text-red-700">{stats.vendorPendingCount} items pending with vendors</div>
                  <div className="text-xs text-red-500 mt-0.5">
                    Goods ordered but not yet received — track via GRN
                  </div>
                  <Link to="/inventory/purchases" className="text-xs text-red-600 underline mt-1 inline-block">
                    View pending deliveries
                  </Link>
                </div>
              </div>
            )}

            {stats.pendingPayments > 0 && (
              <div className="flex items-start gap-3 p-3 rounded-lg bg-red-50 border border-red-100">
                <i className="ri-money-rupee-circle-line text-red-500 text-lg mt-0.5" />
                <div>
                  <div className="text-sm font-medium text-red-700">
                    Rs. {stats.pendingPayments.toLocaleString('en-IN')} pending payments
                  </div>
                  <div className="text-xs text-red-500 mt-0.5">
                    {allBookings.filter((b) => b.pending_amount > 0).length} bookings awaiting payment
                  </div>
                  <Link to="/reception/billing" className="text-xs text-red-600 underline mt-1 inline-block">
                    View pending bills
                  </Link>
                </div>
              </div>
            )}

            {allBookings.some((b) => b.status === 'confirmed' && new Date(b.check_in).toISOString().slice(0, 10) === todayStr) && (
              <div className="flex items-start gap-3 p-3 rounded-lg bg-blue-50 border border-blue-100">
                <i className="ri-calendar-check-line text-blue-600 text-lg mt-0.5" />
                <div>
                  <div className="text-sm font-medium text-blue-800">
                    {allBookings.filter((b) => b.status === 'confirmed' && new Date(b.check_in).toISOString().slice(0, 10) === todayStr).length} arrivals expected today
                  </div>
                  <div className="text-xs text-blue-600 mt-0.5">
                    Check bookings for room preparation
                  </div>
                  <Link to="/bookings/manage" className="text-xs text-blue-700 underline mt-1 inline-block">
                    View arrivals
                  </Link>
                </div>
              </div>
            )}

            {demoRooms.some((r) => r.status === 'maintenance') && (
              <div className="flex items-start gap-3 p-3 rounded-lg bg-gray-50 border border-gray-200">
                <i className="ri-tools-line text-gray-500 text-lg mt-0.5" />
                <div>
                  <div className="text-sm font-medium text-gray-700">
                    {demoRooms.filter((r) => r.status === 'maintenance').length} rooms under maintenance
                  </div>
                  <div className="text-xs text-gray-500 mt-0.5">
                    Room 204 requires plumbing work
                  </div>
                  <Link to="/bookings/rooms" className="text-xs text-gray-600 underline mt-1 inline-block">
                    View rooms
                  </Link>
                </div>
              </div>
            )}

            {stats.cleaningRooms.length > 0 && (
              <div className="flex items-start gap-3 p-3 rounded-lg bg-yellow-50 border border-yellow-100">
                <i className="ri-brush-line text-yellow-600 text-lg mt-0.5" />
                <div>
                  <div className="text-sm font-medium text-yellow-800">
                    {stats.cleaningRooms.length} room{stats.cleaningRooms.length > 1 ? 's' : ''} need{stats.cleaningRooms.length === 1 ? 's' : ''} cleaning
                  </div>
                  <div className="text-xs text-yellow-600 mt-0.5">
                    {stats.cleaningRooms.map((r) => r.room_number).join(', ')} — Mark as ready when done
                  </div>
                  <Link to="/bookings/rooms" className="text-xs text-yellow-700 underline mt-1 inline-block">
                    Go to Rooms page
                  </Link>
                </div>
              </div>
            )}

            {stats.dormFullyBooked > 0 && (
              <div className="flex items-start gap-3 p-3 rounded-lg bg-teal-50 border border-teal-100">
                <i className="ri-hotel-bed-line text-teal-600 text-lg mt-0.5" />
                <div>
                  <div className="text-sm font-medium text-teal-800">
                    {stats.dormFullyBooked} dormitory fully booked
                  </div>
                  <div className="text-xs text-teal-600 mt-0.5">
                    All beds occupied in dormitory rooms
                  </div>
                  <Link to="/bookings/rooms" className="text-xs text-teal-700 underline mt-1 inline-block">
                    View dormitories
                  </Link>
                </div>
              </div>
            )}

            {stats.dormLowBeds > 0 && (
              <div className="flex items-start gap-3 p-3 rounded-lg bg-amber-50 border border-amber-100">
                <i className="ri-alert-line text-amber-600 text-lg mt-0.5" />
                <div>
                  <div className="text-sm font-medium text-amber-800">
                    {stats.dormLowBeds} dormitories running low on beds
                  </div>
                  <div className="text-xs text-amber-600 mt-0.5">
                    Only 1–2 beds available in these rooms
                  </div>
                  <Link to="/bookings/rooms" className="text-xs text-amber-700 underline mt-1 inline-block">
                    Check availability
                  </Link>
                </div>
              </div>
            )}

            {stats.bedsUnderMaintenance > 0 && (
              <div className="flex items-start gap-3 p-3 rounded-lg bg-gray-50 border border-gray-200">
                <i className="ri-tools-line text-gray-500 text-lg mt-0.5" />
                <div>
                  <div className="text-sm font-medium text-gray-700">
                    {stats.bedsUnderMaintenance} bed{stats.bedsUnderMaintenance > 1 ? 's' : ''} under maintenance
                  </div>
                  <div className="text-xs text-gray-500 mt-0.5">
                    Bed 10 in Room D101 needs repair
                  </div>
                  <Link to="/bookings/rooms" className="text-xs text-gray-600 underline mt-1 inline-block">
                    View beds
                  </Link>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}